import { Injectable } from '@angular/core';
import { OrganizationApiManagerService } from '../api/managers/organization-api-manager.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { FunctionsHelperService } from '../../functions-helper.service';
import {
    IOrganIn,
    IOrganInMetadata,
    IOrganization,
    IOrganizationMetadata,
} from '../../../models/organization/organization-model';
import { OrganizationConverterService } from '../converters/organization-converter.service';
import { MissingArgumentsError } from '../../../models/errors/general.errors';
import {
    IWorkspace,
    IWorkspaceMetadata,
    WSAssocUpdateActionType,
    WSUpdateAction,
    WSUpdateAssocEnum,
} from '../../../models/workspace/workspace-model';
import { IBranding } from '../../../models/user';
import { ProfileService } from '../../show/profile.service';
import { AnalyticsNotifierService } from '../../utils/analytics-notifier.service';
import { OrganizationLoadError, organizationLoadError } from '../organization-load-error';

interface IOrganAndDidSwap {
    organization: IOrganization;
    didSwap: boolean;
}

@Injectable()
export class OrganizationStateManagerService {
    private _currentOrganizationSubject = new BehaviorSubject<IOrganAndDidSwap>({ organization: null, didSwap: false });
    public currentOrganization$ = this._currentOrganizationSubject.asObservable();
    private readonly _loadState = new BehaviorSubject<{ loading: boolean; error: OrganizationLoadError | null }>({ loading: false, error: null });
    public loadState$ = this._loadState.asObservable();
    private loadSequence = 0;
    private lastRequestedOrganId: string | null = null;

    private _organsMDSubject = new BehaviorSubject<IOrganizationMetadata[]>(null);
    public organsMD$ = this._organsMDSubject.asObservable();

    private _wsMetadataUpdatedSubject = new Subject<IWorkspaceMetadata>();
    public wsMetadataUpdated$ = this._wsMetadataUpdatedSubject.asObservable();

    private _numberOfWSMetadatasSubject = new BehaviorSubject<number>(null);
    public numberOfWSMetadatas$ = this._numberOfWSMetadatasSubject.asObservable();
    private ORGAN_LS_ID = 'organizationId';
    currentUserEmail: string;

    private _currentOrganIdSubject = new BehaviorSubject<string>(null);
    public currentOrganId$ = this._currentOrganIdSubject.asObservable();

    constructor(
        private orgApiManager: OrganizationApiManagerService,
        private functionsHelper: FunctionsHelperService,
        private organConverter: OrganizationConverterService,
        private profileService: ProfileService,
        private analytics: AnalyticsNotifierService
    ) {
        this.profileService.user$.subscribe((user) => {
            if (!user) {
                return;
            }
            if (this.currentUserEmail && this.currentUserEmail === user.email) {
                return;
            }
            this.currentUserEmail = user.email;
            this.getOrganizationsMDAsync();
        });
    }

    public onNewOrganizationCreated(org: IOrganIn, orgMD: IOrganInMetadata) {
        this.convertMDOrganAndPushToLocal(orgMD);
        this.convertOrganAndSetLocal(org);
    }

    public onOrganizationUpdate(organ: IOrganIn) {
        this.convertOrganAndSetLocal(organ);
    }

    public onBrandingUpdate(branding: IBranding, organId: string) {
        const organData = this._currentOrganizationSubject.value;
        if (!organData) {
            return;
        }
        const { organization } = organData;
        if (!organization) {
            return;
        }
        if (organization.id !== organId) {
            return;
        }
        organization.branding = branding;
        this.convertOrganAndSetLocal(organization);
    }

    public async getOrganizationsMDAsync() {
        const sequence = ++this.loadSequence;
        this._loadState.next({ loading: true, error: null });
        const { data, error } = await this.orgApiManager.getAllOrganizationsMDAsync();
        if (sequence !== this.loadSequence) return;
        if (error) {
            this._loadState.next({ loading: false, error: organizationLoadError(error) });
            return;
        }
        const organizations = data ?? [];
        this.convertMDOrganAndSetLocal(organizations);
        const available = organizations.filter(org => org.canViewOrganization ?? org.isAllowedToUseOrganization);
        if (available.length > 0) {
            let organIndex = 0;
            let savedOrganId = null;
            /// Saved from the user settings
            const isDefaultOrganExisted = available.find((organ) => organ.isDefaultForUser);
            if (isDefaultOrganExisted) {
                savedOrganId = isDefaultOrganExisted.id;
            } else {
                savedOrganId = this.getOrganIdFromLS();
            }
            if (savedOrganId) {
                const savedOrganIndex = available.findIndex((organ) => organ.id === savedOrganId);
                if (savedOrganIndex !== -1) {
                    organIndex = savedOrganIndex;
                }
            }
            await this.pickOrganizationAsync(available[organIndex].id);
        } else {
            this._currentOrganizationSubject.next({ organization: null, didSwap: false });
            this._loadState.next({ loading: false, error: { kind: 'forbidden', message: 'No organizations are available. Accept an invitation to join an organization.', retryable: false } });
        }
    }

    public async pickOrganizationAsync(organId: string) {
        const sequence = ++this.loadSequence;
        this.lastRequestedOrganId = organId;
        this._loadState.next({ loading: true, error: null });
        try {
            const inOrgan = await this.getOrganizationAsync(organId);
            if (sequence !== this.loadSequence) return false;
            this.convertOrganAndSetLocal(inOrgan);
            this._loadState.next({ loading: false, error: null });
            return true;
        } catch (error: unknown) {
            if (sequence !== this.loadSequence) return false;
            this._loadState.next({ loading: false, error: organizationLoadError(error) });
            return false;
        }
    }

    public retryOrganizationLoad() {
        return this.lastRequestedOrganId ? this.pickOrganizationAsync(this.lastRequestedOrganId) : this.getOrganizationsMDAsync();
    }

    public onWorkspacesUpdate(organ: IOrganIn) {
        this.convertAndSetOrganIfCurrent(organ);
    }

    public onWSMetadataUpdate(workspace: IWorkspace, action: WSUpdateAction) {
        const { organization: currentOrag } = this._currentOrganizationSubject.value;
        if (!currentOrag) {
            return;
        }
        const { workspaces } = currentOrag;
        const currentWS = workspaces.find((ws) => ws.id === workspace.id);
        if (!currentWS) {
            return;
        }
        switch (action) {
            case 'nameUpdated':
                currentWS.name = workspace.name;
                break;
        }
        this.setCurrentOrgan(currentOrag);
    }

    public onWSAssociationUpdate(wsId: string, type: WSUpdateAssocEnum, action: WSAssocUpdateActionType) {
        const { organization: currentOrag } = this._currentOrganizationSubject.value;
        if (!currentOrag) {
            return;
        }
        const { workspaces } = currentOrag;
        const currentWS = workspaces.find((ws) => ws.id === wsId);
        if (!currentWS) {
            return;
        }
        switch (type) {
            case WSUpdateAssocEnum.PROJECT:
                switch (action) {
                    case 'addAssoc':
                        currentWS.projectCount = currentWS.projectCount + 1;
                        break;
                    case 'removeAssoc':
                        currentWS.projectCount = currentWS.projectCount - 1;
                        break;
                }
                break;
            case WSUpdateAssocEnum.ASSET:
                switch (action) {
                    case 'addAssoc':
                        currentWS.assetCount = currentWS.assetCount + 1;
                        break;
                    case 'removeAssoc':
                        currentWS.assetCount = currentWS.assetCount - 1;
                        break;
                }
                break;
            case WSUpdateAssocEnum.USER:
                switch (action) {
                    case 'addAssoc':
                        currentWS.userCount = currentWS.userCount + 1;
                        break;
                    case 'removeAssoc':
                        currentWS.userCount = currentWS.userCount - 1;
                        break;
                }
                break;
            case WSUpdateAssocEnum.TEMPLATE:
                switch (action) {
                    case 'addAssoc':
                        currentWS.templateCount = currentWS.templateCount + 1;
                        break;
                    case 'removeAssoc':
                        currentWS.templateCount = currentWS.templateCount - 1;
                        break;
                }
                break;
        }
        this.setCurrentOrgan(currentOrag);
        this._wsMetadataUpdatedSubject.next(currentWS);
    }

    public onOrganizationMetadataUpdate(org: IOrganInMetadata) {
        const currentMetadatas = this._organsMDSubject.value;
        const index = currentMetadatas.findIndex((meta) => meta.id === org.id);

        if (index === -1) {
            return;
        }

        currentMetadatas[index] = { ...currentMetadatas[index], ...org };
        this.setCurrentMDOrgan(currentMetadatas);
    }

    public getCurrentOrganizationAsync() {
        return this.functionsHelper.getDataAsync(this._currentOrganizationSubject);
    }

    private convertAndSetOrganIfCurrent(inOrgan: IOrganIn) {
        const { organization: currentOrag } = this._currentOrganizationSubject.value;
        if (!currentOrag || currentOrag.id !== inOrgan?.id) {
            return;
        }
        this.convertOrganAndSetLocal(inOrgan);
    }

    private convertOrganAndSetLocal(organ: IOrganIn) {
        const localOrgan = this.organConverter.inToLocal(organ);
        this.setCurrentOrgan(localOrgan);
    }

    private convertMDOrganAndPushToLocal(organ: IOrganInMetadata) {
        const currentOrgansMD = this._organsMDSubject.value ?? [];

        const localOrgan = this.organConverter.inMDToLocal(organ);
        currentOrgansMD.push(localOrgan);
        this.setCurrentMDOrgan(currentOrgansMD);
    }

    private convertMDOrganAndSetLocal(organs: IOrganInMetadata[]) {
        const localOrgan = organs.map((org) => this.organConverter.inMDToLocal(org));
        this.setCurrentMDOrgan(localOrgan);
    }

    private async getOrganizationAsync(organId: string) {
        const { data, error } = await this.orgApiManager.getOrganizationAsync(organId);
        if (error) {
            throw error;
        }
        return data;
    }

    private setCurrentOrgan(organ: IOrganization) {
        if (!organ) {
            throw new MissingArgumentsError(`Could not set current organization`);
        }
        let isSwapping: boolean = false;
        const currentOrgan = this._currentOrganizationSubject.value;
        if (currentOrgan?.organization?.id !== organ.id) {
            isSwapping = true;
            if (currentOrgan?.organization) {
                this.analytics.notifyEvent('Organization - Swap', {
                    oldOrganizationName: currentOrgan.organization.name,
                    newOrganizationName: organ.name,
                });
            }
        }
        this._numberOfWSMetadatasSubject.next(organ.workspacesMeta.total);
        this.orgApiManager.setOrgContext({ organizationId: organ.id });
        const knownOrganizations = this._organsMDSubject.value ?? [];
        if (!knownOrganizations.some(known => known.id === organ.id)) {
            this.setCurrentMDOrgan([...knownOrganizations, this.organConverter.inMDToLocal({
                ...organ, workspaces: [], isAllowedToUseOrganization: organ.canViewOrganization === true, isDefaultForUser: false,
            })]);
        }
        this._currentOrganIdSubject.next(organ.id);
        this._currentOrganizationSubject.next({ organization: organ, didSwap: isSwapping });

        this.setLocalStorageWSId(organ.id);
    }

    private setCurrentMDOrgan(organMDs: IOrganizationMetadata[]) {
        if (!organMDs) {
            throw new MissingArgumentsError(`Could not set metadata organizations`);
        }
        this._organsMDSubject.next(organMDs);
    }

    private getOrganIdFromLS() {
        return localStorage.getItem(this.ORGAN_LS_ID);
    }

    private setLocalStorageWSId(organId: string) {
        localStorage.setItem(this.ORGAN_LS_ID, organId);
    }
}
